import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  createGallery,
  deleteGallery,
  fetchAllGalleries,
  updateGallery,
  reorderGalleries,
} from '../../controllers/allGalleryController';
import { fetchAllMeetings } from '../../controllers/meetingsController';
import { searchClubEvents } from '../../controllers/clubEventsController';
import { resolveGalleryAssetUrl } from '../../api/httpClientGalleries';
import {
  GALLERY_ENTITY_TYPE,
  GALLERY_ENTITY_TYPE_OPTIONS,
  galleryEntityTypeLabel,
} from '../../constants/galleryEntityTypes';

const tableHeadCellSx = {
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
};

const tableContainerPaperSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f5f5f5',
  borderRadius: '8px',
  overflow: 'hidden',
  py: 1,
  px: 2.5,
};

const mergePageReorder = (globalIds, page, pageSize, reorderedPageIds) => {
  const start = (page - 1) * pageSize;
  const end = start + reorderedPageIds.length;
  return [
    ...globalIds.slice(0, start),
    ...reorderedPageIds,
    ...globalIds.slice(end),
  ];
};

const fetchGlobalGalleryIds = async (knownTotalCount = 0) => {
  const fetchSize = knownTotalCount > 0 ? knownTotalCount : 1000;
  const { data } = await fetchAllGalleries({ pageNumber: 1, pageSize: fetchSize });
  return (Array.isArray(data) ? data : []).map((gallery) => gallery.id);
};

const Galleries = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [saving, setSaving] = useState(false);
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterPool, setFilterPool] = useState('');

  const [form, setForm] = useState({
    id: null,
    title: '',
    description: '',
    relatedEntityType: GALLERY_ENTITY_TYPE.Standalone,
    relatedEntityId: null,
    showOnDetailPage: true,
    showInGalleryPool: true,
  });
  const [formError, setFormError] = useState('');

  const [entityQuery, setEntityQuery] = useState('');
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const navigate = useNavigate();

  const ids = useMemo(() => (items || []).map((item) => item.id), [items]);

  const SortableRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };
    return children({ attributes, listeners, setNodeRef, style });
  };

  const isEntityRequired =
    form.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting
    || form.relatedEntityType === GALLERY_ENTITY_TYPE.ClubEvent;

  const loadGalleries = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const params = { pageNumber: targetPage, pageSize };
      if (filterEntityType !== '') params.relatedEntityType = Number(filterEntityType);
      if (filterPool !== '') params.showInGalleryPool = filterPool === 'true';
      const { data, meta } = await fetchAllGalleries(params);
      setItems(Array.isArray(data) ? data : []);
      setTotalPages(meta?.totalPages || 1);
      setTotalCount(meta?.totalCount ?? meta?.totalItems ?? (Array.isArray(data) ? data.length : 0));
    } catch (e) {
      setError(e?.message || 'Galeriler alınamadı');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGalleries(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterEntityType, filterPool]);

  useEffect(() => {
    if (!createOpen || !isEntityRequired || entityQuery.trim().length < 2) {
      setEntityOptions([]);
      return;
    }

    let active = true;
    setEntityLoading(true);
    (async () => {
      try {
        if (form.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting) {
          const { data } = await fetchAllMeetings({ pageNumber: 1, pageSize: 20, searchTerm: entityQuery.trim() });
          if (!active) return;
          setEntityOptions((data || []).map((m) => ({ id: m.id, label: m.title || '-' })));
        } else if (form.relatedEntityType === GALLERY_ENTITY_TYPE.ClubEvent) {
          const { data } = await searchClubEvents({ keyword: entityQuery.trim(), pageNumber: 1, pageSize: 20 });
          if (!active) return;
          setEntityOptions((data || []).map((e) => ({ id: e.id, label: e.title || '-' })));
        }
      } catch (e) {
        if (!active) return;
        setEntityOptions([]);
      } finally {
        if (active) setEntityLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [createOpen, isEntityRequired, entityQuery, form.relatedEntityType]);

  const openCreate = () => {
    setForm({
      id: null,
      title: '',
      description: '',
      relatedEntityType: GALLERY_ENTITY_TYPE.Standalone,
      relatedEntityId: null,
      showOnDetailPage: true,
      showInGalleryPool: true,
    });
    setSelectedEntity(null);
    setEntityOptions([]);
    setEntityQuery('');
    setFormError('');
    setCreateOpen(true);
  };

  const openEdit = async (gallery) => {
    setForm({
      id: gallery.id,
      title: gallery.title || '',
      description: gallery.description || '',
      relatedEntityType: Number(gallery.relatedEntityType),
      relatedEntityId: gallery.relatedEntityId || null,
      showOnDetailPage: gallery.showOnDetailPage !== false,
      showInGalleryPool: gallery.showInGalleryPool !== false,
    });
    setSelectedEntity(null);
    setEntityOptions([]);
    setEntityQuery('');
    setFormError('');
    setCreateOpen(true);

    if (gallery.relatedEntityId) {
      setSelectedEntity({ id: gallery.relatedEntityId, label: `ID: ${gallery.relatedEntityId}` });
    }
  };

  const handleSave = async () => {
    setFormError('');
    if (isEntityRequired && !form.relatedEntityId) {
      setFormError('Bu galeri tipi için Etkinlik/Toplantı seçimi zorunludur.');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await updateGallery({
          id: form.id,
          title: form.title?.trim() || null,
          showOnDetailPage: Boolean(form.showOnDetailPage),
          showInGalleryPool: Boolean(form.showInGalleryPool),
        });
        toast.success('Galeri güncellendi');
      } else {
        const payload = {
          title: form.title?.trim() || null,
          description: form.description?.trim() || null,
          relatedEntityType: Number(form.relatedEntityType),
          showOnDetailPage: Boolean(form.showOnDetailPage),
          showInGalleryPool: Boolean(form.showInGalleryPool),
          createdById: '00000000-0000-0000-0000-000000000000',
        };
        if (isEntityRequired) payload.relatedEntityId = form.relatedEntityId;
        await createGallery(payload);
        toast.success('Galeri oluşturuldu');
      }
      setCreateOpen(false);
      await loadGalleries();
    } catch (e) {
      setFormError(e?.message || 'Galeri kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await deleteGallery(selected.id);
      toast.success('Galeri silindi');
      setDeleteOpen(false);
      setSelected(null);
      await loadGalleries();
    } catch (e) {
      toast.error(e?.message || 'Galeri silinemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#444444' }}>Galeriler</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ width: 16 }} />}
          onClick={openCreate}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E7EDF4',
            boxShadow: 'none',
            fontSize: 13,
            borderRadius: '5px',
            color: '#0E4D94',
            '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
          }}
        >
          Yeni Galeri
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Galeri tipi"
          value={filterEntityType}
          onChange={(e) => { setFilterEntityType(e.target.value); setPage(1); }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Tümü</MenuItem>
          {GALLERY_ENTITY_TYPE_OPTIONS.map((t) => (
            <MenuItem key={t.value} value={String(t.value)}>{t.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Galeri havuzu"
          value={filterPool}
          onChange={(e) => { setFilterPool(e.target.value); setPage(1); }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Tümü</MenuItem>
          <MenuItem value="true">Havuzda gösterilen</MenuItem>
          <MenuItem value="false">Havuzda gizli</MenuItem>
        </TextField>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableContainer component={Paper} elevation={0} sx={tableContainerPaperSx}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'transparent' }}>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, width: 80 }}>Sıralama</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 72 }}>Kapak</TableCell>
                <TableCell sx={tableHeadCellSx}>Başlık</TableCell>
                <TableCell sx={tableHeadCellSx}>Tip</TableCell>
                <TableCell sx={tableHeadCellSx}>Görsel</TableCell>
                <TableCell sx={tableHeadCellSx}>Havuz</TableCell>
                <TableCell sx={tableHeadCellSx}>Oluşturulma</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow
                  key={`sk-${index}`}
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: index === 4 ? 'none' : '1px dashed #e8e8e8',
                      px: 1.5,
                      py: 1.5,
                    },
                  }}
                >
                  <TableCell><Skeleton width={24} height={24} borderRadius={4} /></TableCell>
                  <TableCell><Skeleton width={48} height={48} borderRadius={4} /></TableCell>
                  <TableCell><Skeleton width={180} height={20} /></TableCell>
                  <TableCell><Skeleton width={100} height={20} /></TableCell>
                  <TableCell><Skeleton width={40} height={20} /></TableCell>
                  <TableCell><Skeleton width={60} height={20} /></TableCell>
                  <TableCell><Skeleton width={140} height={20} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Skeleton width={32} height={32} borderRadius={6} />
                      <Skeleton width={32} height={32} borderRadius={6} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : items.length === 0 ? (
        <TableContainer component={Paper} elevation={0} sx={tableContainerPaperSx}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'transparent' }}>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, width: 80 }}>Sıralama</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 72 }}>Kapak</TableCell>
                <TableCell sx={tableHeadCellSx}>Başlık</TableCell>
                <TableCell sx={tableHeadCellSx}>Tip</TableCell>
                <TableCell sx={tableHeadCellSx}>Görsel</TableCell>
                <TableCell sx={tableHeadCellSx}>Havuz</TableCell>
                <TableCell sx={tableHeadCellSx}>Oluşturulma</TableCell>
                <TableCell align="right" sx={tableHeadCellSx}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>Galeri bulunamadı.</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIndex = ids.indexOf(active.id);
            const newIndex = ids.indexOf(over.id);
            const updatedPageItems = arrayMove(items, oldIndex, newIndex);
            setItems(updatedPageItems);

            (async () => {
              try {
                const globalIds = await fetchGlobalGalleryIds(totalCount);
                const reorderedPageIds = updatedPageItems.map((item) => item.id);
                const galleryIdsInOrder = mergePageReorder(globalIds, page, pageSize, reorderedPageIds);
                await reorderGalleries({ galleryIdsInOrder });
                toast.success('Galeri sıralaması güncellendi');
              } catch (e) {
                toast.error(e?.message || 'Sıralama güncellenemedi');
                try {
                  await loadGalleries(page);
                } catch (_) { /* ignore */ }
              }
            })();
          }}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <TableContainer component={Paper} elevation={0} sx={tableContainerPaperSx}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={{ ...tableHeadCellSx, width: 80 }}>Sıralama</TableCell>
                    <TableCell sx={{ ...tableHeadCellSx, width: 72 }}>Kapak</TableCell>
                    <TableCell sx={tableHeadCellSx}>Başlık</TableCell>
                    <TableCell sx={tableHeadCellSx}>Tip</TableCell>
                    <TableCell sx={tableHeadCellSx}>Görsel</TableCell>
                    <TableCell sx={tableHeadCellSx}>Havuz</TableCell>
                    <TableCell sx={tableHeadCellSx}>Oluşturulma</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <SortableRow key={item.id} id={item.id}>
                      {({ attributes, listeners, setNodeRef, style }) => (
                        <TableRow
                          ref={setNodeRef}
                          style={style}
                          {...attributes}
                          hover
                          sx={{
                            cursor: 'pointer',
                            '& .MuiTableCell-root': {
                              borderBottom: index === items.length - 1 ? 'none' : '1px dashed #e8e8e8',
                              px: 1.5,
                              py: 1.5,
                              color: '#444444',
                              fontSize: '14px',
                            },
                            '&:hover': { backgroundColor: '#fafafa' },
                          }}
                          onClick={() => navigate(`/galleries/${item.id}`)}
                        >
                          <TableCell
                            {...listeners}
                            sx={{ cursor: 'grab', userSelect: 'none', width: 80 }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <DragIndicatorIcon sx={{ color: '#a3a3a3', fontSize: 20 }} />
                          </TableCell>
                          <TableCell>
                            {item.coverImageUrl ? (
                              <Box
                                component="img"
                                src={resolveGalleryAssetUrl(item.coverImageUrl)}
                                alt={item.title || 'Kapak'}
                                sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '6px', border: '1px solid #e6e6e6' }}
                              />
                            ) : (
                              <Box sx={{ width: 48, height: 48, borderRadius: '6px', backgroundColor: '#f5f5f5', border: '1px solid #e6e6e6' }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{item.title || '-'}</TableCell>
                          <TableCell>{galleryEntityTypeLabel(item.relatedEntityType)}</TableCell>
                          <TableCell>{item.imageCount ?? 0}</TableCell>
                          <TableCell>{item.showInGalleryPool !== false ? 'Evet' : 'Hayır'}</TableCell>
                          <TableCell>
                            {item.createdAt ? new Date(item.createdAt).toLocaleString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Düzenle">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(item);
                                  }}
                                  sx={{
                                    backgroundColor: '#E7EDF4',
                                    borderRadius: '6px',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { backgroundColor: '#CFDBEA' },
                                    '& svg': { fontSize: '16px', color: '#0E4D94' },
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelected(item);
                                    setDeleteOpen(true);
                                  }}
                                  sx={{
                                    backgroundColor: '#FEF0F2',
                                    borderRadius: '6px',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { backgroundColor: '#FDD9E1' },
                                    '& svg': { fontSize: '16px', color: '#F8285A' },
                                  }}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </SortableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SortableContext>
        </DndContext>
      )}

      {!loading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} />
        </Box>
      )}

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          {form.id ? 'Galeri Düzenle' : 'Yeni Galeri'}
          <IconButton
            onClick={() => !saving && setCreateOpen(false)}
            size="small"
            disabled={saving}
            sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            {formError && <Alert severity="error" sx={{ borderRadius: '8px' }}>{formError}</Alert>}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Başlık</Typography>
              <TextField
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                fullWidth
                size="small"
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' } },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Açıklama</Typography>
              <TextField
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder=""
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' } },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Galeri Tipi</Typography>
              <TextField
                select
                value={form.relatedEntityType}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setForm((prev) => ({ ...prev, relatedEntityType: val, relatedEntityId: null }));
                  setSelectedEntity(null);
                  setEntityQuery('');
                  setEntityOptions([]);
                }}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' } },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                }}
              >
                {GALLERY_ENTITY_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.showOnDetailPage}
                    onChange={(e) => setForm((prev) => ({ ...prev, showOnDetailPage: e.target.checked }))}
                  />
                }
                label="Detay sayfasında göster"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.showInGalleryPool}
                    onChange={(e) => setForm((prev) => ({ ...prev, showInGalleryPool: e.target.checked }))}
                  />
                }
                label="Galeri havuzunda göster"
              />
            </Stack>

            {isEntityRequired && (
              <Autocomplete
                options={entityOptions}
                getOptionLabel={(o) => o?.label || ''}
                value={selectedEntity}
                onChange={(_, v) => {
                  setSelectedEntity(v);
                  setForm((prev) => ({ ...prev, relatedEntityId: v?.id || null }));
                }}
                onInputChange={(_, v) => setEntityQuery(v)}
                loading={entityLoading}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={form.relatedEntityType === GALLERY_ENTITY_TYPE.Meeting ? 'Toplantı Seç' : 'Etkinlik Seç'}
                    placeholder="İsme göre ara..."
                    size="small"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '5px', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' } },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {entityLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': { backgroundColor: '#0a3d7a' },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Galeriyi Sil
          <IconButton
            onClick={() => !saving && setDeleteOpen(false)}
            size="small"
            disabled={saving}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            {selected ? `"${selected.title || 'Seçili galeri'}" silinsin mi?` : 'Seçili galeri silinsin mi?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              px: 2,
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Galleries;

